import { useCallback, useEffect, useMemo, useState } from 'react';
import { getPointTasks } from '../../../../auth/_requests';
import type { Offer, PointCategoryName } from '../../../../helpers/utils/types';

export type RewardsPointTasksByCategoryState = {
  loadingByCategory: Record<string, boolean>;
  tasksByCategory: Record<string, Offer[]>;
  error?: string;
  fetchCategory: (categoryName: PointCategoryName) => Promise<void>;
};

export const usePointTasksByCategory = (opts: { token?: string; enabled: boolean }) => {
  const { token, enabled } = opts;
  const [loadingByCategory, setLoadingByCategory] = useState<Record<string, boolean>>({});
  const [tasksByCategory, setTasksByCategory] = useState<Record<string, Offer[]>>({});
  const [error, setError] = useState<string | undefined>(undefined);

  const fetchCategory = useCallback(
    async (categoryName: PointCategoryName) => {
      if (!token) return;
      setError(undefined);
      setLoadingByCategory((prev) => ({ ...prev, [categoryName]: true }));
      try {
        const tasks = await getPointTasks(token, categoryName);
        setTasksByCategory((prev) => ({ ...prev, [categoryName]: tasks }));
      } catch (e) {
        console.error('Error fetching point tasks by category:', e);
        setTasksByCategory((prev) => ({ ...prev, [categoryName]: [] }));
        setError(e instanceof Error ? e.message : 'Unknown error');
      } finally {
        setLoadingByCategory((prev) => ({ ...prev, [categoryName]: false }));
      }
    },
    [token]
  );

  useEffect(() => {
    if (!enabled) return;
    // keep cache; no automatic fetch until a category is expanded
  }, [enabled]);

  return useMemo<RewardsPointTasksByCategoryState>(
    () => ({ loadingByCategory, tasksByCategory, error, fetchCategory }),
    [loadingByCategory, tasksByCategory, error, fetchCategory]
  );
};

